import { Injectable } from '@angular/core';
import { Auth, authState } from '@angular/fire/auth';
import { Firestore, doc, docData } from '@angular/fire/firestore';
import { BehaviorSubject, EMPTY, Observable, firstValueFrom } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { UserAbility } from '../../core/models/user-profile';
import { RbacService } from './rbac.service';
import { AppConfigService } from './app-config.service';
import { CrudOperation, RbacModule, ROUTE_TO_RBAC, requiredRouteOperation, resolveRouteRbacMapping } from '../models/rbac.models';
import { MODULE_LEGACY_MENU_ROLES } from '../models/role-sets';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {

  private abilitySubject = new BehaviorSubject<UserAbility | null>(null);
  ability$ = this.abilitySubject.asObservable();
  private loaded = false;

  constructor(
    private auth: Auth,
    private firestore: Firestore,
    private rbacService: RbacService,
    private appConfig: AppConfigService
  ) {
    // #1976 — l'écoute de `employees/{uid}` est RATTACHÉE à l'état de
    // connexion : `switchMap` la ferme dès que l'utilisateur change ou se
    // déconnecte. Avant, chaque connexion ouvrait une écoute qui n'était
    // jamais fermée : à la déconnexion, Firestore la rejouait sans identité et
    // les règles la refusaient (« Missing or insufficient permissions » dans la
    // console), et une reconnexion en ajoutait une de plus.
    authState(this.auth).pipe(
      switchMap(user => {
        if (!user) {
          this.abilitySubject.next(null);
          this.loaded = false;
          return EMPTY;
        }
        // Load RBAC permissions in parallel
        this.rbacService.loadCurrentUserPermissions(user.uid);
        return this.employeeDoc$(user.uid).pipe(
          // Une erreur ne doit ni remonter au gestionnaire global, ni tuer
          // l'abonnement à `authState` (la connexion suivante n'aurait plus de
          // rôle). Après une déconnexion, le refus est attendu : silence.
          catchError(err => {
            if (this.auth.currentUser) {
              console.warn('[PermissionService] Lecture du rôle impossible :', err?.code || err);
            }
            return EMPTY;
          }),
        );
      }),
    ).subscribe((profile: any) => {
      const ability = profile?.role || profile?.ability || null;
      this.abilitySubject.next(ability);
      this.loaded = true;
    });
  }

  /** Écoute temps réel de la fiche employé — couture isolée pour les tests. */
  private employeeDoc$(uid: string): Observable<unknown> {
    return docData(doc(this.firestore, 'employees', uid));
  }

  /**
   * #812 — flag RBAC dynamique lu depuis app_config/main (défaut false).
   * Rollback instantané : repasser rbac.enabled à false dans le doc, sans deploy.
   */
  get rbacEnabled(): boolean {
    return this.appConfig.rbacEnabled;
  }

  get currentAbility(): UserAbility | null {
    return this.abilitySubject.value;
  }

  /**
   * #2050 — rôle de l'employé, attendu le temps que sa fiche se charge (borné) :
   * une requête lancée au chargement à froid doit connaître le rôle AVANT de
   * choisir ses filtres (le support n'a droit qu'aux dossiers clients).
   * Expiration ⇒ `null`, la requête part alors sans filtre de rôle.
   */
  async abilityWhenLoaded(timeoutMs = 5000): Promise<UserAbility | null> {
    if (this.abilitySubject.value) return this.abilitySubject.value;
    return Promise.race([
      firstValueFrom(this.ability$.pipe(filter((a): a is UserAbility => !!a), take(1))),
      new Promise<null>(resolve => setTimeout(() => resolve(null), timeoutMs)),
    ]);
  }

  // ══════════════════════════════════════════
  // RBAC RESOLUTION (#812)
  // ══════════════════════════════════════════
  // Les clés subModule sont alignées sur routeToKey(route) de NAV_SECTIONS —
  // la source des clés écrites par l'éditeur de matrice (rbac-settings /
  // RBAC_SUBMODULES). Une clé inventée ici ne matcherait jamais une règle.

  /** La matrice gouverne ce module : flag actif ET au moins une règle ACL active sur le module. */
  private useRbac(module: RbacModule): boolean {
    return this.rbacEnabled && this.rbacService.isModuleCovered(module);
  }

  /**
   * Fallback sûr PAR module : flag off, permissions non chargées ou module
   * absent de la matrice ⇒ comportement legacy. Une matrice incomplète ne
   * doit jamais produire de lockout (décision ticket #812).
   */
  private rbacOrLegacy(
    module: RbacModule,
    subModule: string,
    operation: CrudOperation,
    legacy: () => boolean
  ): boolean {
    if (this.useRbac(module)) {
      return this.isAdmin() || this.rbacService.can(module, subModule, operation);
    }
    return legacy();
  }

  // ══════════════════════════════════════════
  // ADMIN / RH BASE CHECKS
  // ══════════════════════════════════════════

  isSuperAdmin(): boolean {
    return this.currentAbility === 'super_admin';
  }

  isAdmin(): boolean {
    return this.currentAbility === 'admin' || this.isSuperAdmin();
  }

  isRH(): boolean {
    return this.currentAbility === 'rh';
  }

  isAdminOrRH(): boolean {
    return this.isAdmin() || this.isRH();
  }

  // ══════════════════════════════════════════
  // TEAM MANAGEMENT
  // ══════════════════════════════════════════

  canCreateInternalMember(): boolean {
    return this.rbacOrLegacy('hr', 'directory', 'create', () => this.isAdminOrRH());
  }

  canEditInternalMember(): boolean {
    return this.rbacOrLegacy('hr', 'directory', 'update', () => this.isAdminOrRH());
  }

  /** Changer le rôle d'un employé = admin-only dans les deux mondes (pas de clé matrice dédiée). */
  canEditRole(): boolean {
    return this.isAdmin();
  }

  canDeleteInternalMember(): boolean {
    return this.rbacOrLegacy('hr', 'directory', 'delete', () => this.isAdmin());
  }

  /**
   * #2073 — supprimer une fiche `employees/` : admin ET RH (décision du 29/10). La
   * suppression passe par la fonction tracée `deleteEmployee` (palier `hr`, motif
   * exigé), qui refuse en outre toute fiche privilégiée hors super_admin.
   * Distinct de `canDeleteInternalMember`, qui garde aussi l'équipe logistique.
   */
  canDeleteEmployeeRecord(): boolean {
    return this.rbacOrLegacy('hr', 'directory', 'delete', () => this.isAdminOrRH());
  }

  canInviteEmployee(): boolean {
    return this.rbacOrLegacy('hr', 'directory', 'create', () => this.isAdminOrRH());
  }

  // ── Membres externes (page Équipe logistique — nav hr/logistics-team) ──

  canCreateExternalMember(): boolean {
    return this.rbacOrLegacy('hr', 'logistics_team', 'create',
      () => this.isAdmin() || this.currentAbility === 'topManager');
  }

  canEditExternalMember(): boolean {
    return this.rbacOrLegacy('hr', 'logistics_team', 'update',
      () => this.isAdmin() || this.currentAbility === 'topManager');
  }

  // ── Partenaires expédition (page logistics_europe/shipping-partners ;
  //    les pages partenaires livraison Africa sont gardées par les routes) ──

  canCreatePartner(): boolean {
    return this.rbacOrLegacy('logistics_europe', 'shipping_partners', 'create', () => {
      const a = this.currentAbility;
      return this.isAdmin() || a === 'topManager' || a === 'manager';
    });
  }

  canEditPartner(): boolean {
    return this.rbacOrLegacy('logistics_europe', 'shipping_partners', 'update', () => {
      const a = this.currentAbility;
      return this.isAdmin() || a === 'topManager' || a === 'manager';
    });
  }

  canDeletePartner(): boolean {
    return this.rbacOrLegacy('logistics_europe', 'shipping_partners', 'delete', () => this.isAdmin());
  }

  // ── Partenaires LIVRAISON RDC (page logistics_africa/partners) ──

  /**
   * #921 — tenir le registre des livreurs RDC est un acte de RESPONSABLE
   * (décision produit du 2026-07-28 : ni warehouse, ni transitory, ni delivery).
   *
   * La LISTE reste ouverte à toute la chaîne de livraison (route `partners` sur
   * DELIVERY_ROLES, rule de lecture `isEmployee()`) : sans ce garde, les boutons
   * « Nouveau partenaire » et le clic sur une ligne mèneraient les rangs
   * terrain vers un écran que `StaffAuthGuard` leur refuse — un bouton qui
   * conduit à un refus est un bug d'UX.
   *
   * Aligné sur trois choses qui doivent bouger ensemble : `isManager()` dans la
   * rule `deliveryPartners`, `PARTNER_REGISTRY_ROLES` sur `partners/new` et
   * `partners/:id`, et ce garde d'affichage.
   */
  canManageDeliveryPartner(): boolean {
    return this.rbacOrLegacy('logistics_africa', 'partners', 'update', () => {
      const a = this.currentAbility;
      return this.isAdmin() || a === 'topManager' || a === 'manager';
    });
  }

  // ── CRM ──

  /** Liaison contact ↔ portail B2B (#573) — aligné sur le gate manager de la CF linkContactToAuthUser */
  canManagePortalLink(): boolean {
    return this.rbacOrLegacy('crm', 'crm_accounts', 'update', () => {
      const a = this.currentAbility;
      return this.isAdmin() || a === 'topManager' || a === 'manager' || a === 'rh';
    });
  }

  /**
   * Case « accepte les communications marketing » d'une fiche contact (#1764).
   * Alignée sur `isManager()` des rules `crm_contacts` (update réservé
   * manager+) : un profil qui ne peut pas écrire ne voit pas la case active.
   */
  canEditMarketingConsent(): boolean {
    return this.rbacOrLegacy('crm', 'crm_accounts', 'update', () => {
      const a = this.currentAbility;
      return this.isAdmin() || a === 'topManager' || a === 'manager';
    });
  }

  // ── Base de connaissances (#581/#584) ──

  /**
   * Aligné sur isManager() des rules : publication/approbation/suppression
   * KB réservées manager+ (super_admin/admin/manager/topManager).
   */
  canManageKnowledge(): boolean {
    return this.rbacOrLegacy('knowledge', 'knowledge_articles', 'update', () => {
      const a = this.currentAbility;
      return this.isAdmin() || a === 'manager' || a === 'topManager';
    });
  }

  // ══════════════════════════════════════════
  // HR MODULE
  // ══════════════════════════════════════════

  canViewHRDashboard(): boolean {
    return this.rbacOrLegacy('hr', 'hr_dashboard', 'read',
      () => this.isAdminOrRH() || this.currentAbility === 'manager' || this.currentAbility === 'topManager');
  }

  canManageAttendance(): boolean {
    return this.rbacOrLegacy('hr', 'attendance', 'update',
      () => this.isAdminOrRH() || this.currentAbility === 'manager' || this.currentAbility === 'topManager');
  }

  /** Self-service : jamais gouverné par la matrice (verrouillerait le pointage de tout le monde). */
  canSelfCheckIn(): boolean {
    return !!this.currentAbility; // tout employé connecté
  }

  canCreateAnnouncement(): boolean {
    return this.rbacOrLegacy('hr', 'announcements', 'create',
      () => this.isAdminOrRH() || this.currentAbility === 'topManager');
  }

  canManageOnboarding(): boolean {
    return this.rbacOrLegacy('hr', 'onboarding', 'update', () => this.isAdminOrRH());
  }

  /** Self-service : jamais gouverné par la matrice (tout employé peut poser un congé). */
  canSubmitLeaveRequest(): boolean {
    return !!this.currentAbility;
  }

  canApproveLeaveRequest(): boolean {
    return this.rbacOrLegacy('hr', 'leave_approvals', 'update',
      () => this.isAdminOrRH() || this.currentAbility === 'manager' || this.currentAbility === 'topManager');
  }

  // ══════════════════════════════════════════
  // RBAC-SPECIFIC (new methods)
  // ══════════════════════════════════════════

  /** Administrer le RBAC lui-même = admin-only dans les deux mondes (jamais délégué via la matrice). */
  canManageRbac(): boolean {
    return this.isAdmin();
  }

  canManageDepartments(): boolean {
    return this.rbacOrLegacy('hr', 'departments', 'update', () => this.isAdminOrRH());
  }

  /**
   * Generic RBAC check — used for dynamic module/page access control.
   * Falls back to legacy (admin/topManager) when RBAC is disabled or the
   * module is not covered by the matrix.
   */
  canAccess(module: string, subModule: string, operation: CrudOperation): boolean {
    return this.rbacOrLegacy(module as RbacModule, subModule, operation,
      () => this.isAdmin() || this.currentAbility === 'topManager');
  }

  /**
   * Check if user can access any sub-module of a given module.
   * Used to show/hide entire sidebar sections.
   * When RBAC is disabled (or the module uncovered), uses legacy role-based rules.
   */
  canAccessModule(module: RbacModule): boolean {
    if (this.useRbac(module)) {
      return this.isAdmin() || this.rbacService.canAny(module);
    }
    // Legacy fallback: visibilité par section pilotée par le référentiel
    // UNIQUE de role-sets.ts (#813) — même source que les data.roles des
    // routes et que le seed ACL. Plus de switch dupliqué.
    const ability = this.currentAbility;
    if (!ability) return false;
    // Super admin, admin, topManager see everything
    if (ability === 'super_admin' || ability === 'admin' || ability === 'topManager') return true;

    return (MODULE_LEGACY_MENU_ROLES[module] ?? []).includes(ability);
  }

  /**
   * #2023 — envoyer un SMS du service client (renvoi d'étiquette) : même contrôle
   * que le serveur (`assertCustomerServiceCaller`, `sendLogisticsNotification`).
   * `manager` et au-dessus avec le module `customer_service`, matrice comprise, ou
   * l'agent `support`, que la matrice ne peut pas retirer côté serveur non plus.
   * L'écran ne fait que masquer l'option : c'est le serveur qui refuse.
   */
  canSendCustomerServiceSms(): boolean {
    return this.canAccessModule('customer_service') || this.currentAbility === 'support';
  }

  /**
   * #813 — décision d'accès route pour StaffAuthGuard (RBAC étape 2).
   *
   * La matrice gouverne quand : flag actif ET mapping résolu (explicite
   * `data.rbac`, sinon préfixe ROUTE_TO_RBAC) ET module couvert ET module
   * ≠ 'admin' (les écrans d'administration — seed, config plateforme, RBAC
   * lui-même — ne sont JAMAIS délégués via la matrice : legacy admin-only).
   * Sémantique (#935, convention du 04/08) : accéder à un écran exige le verbe
   * de l'écran — `create` pour un formulaire de création (`…/new`), `update`
   * pour une édition (`…/edit` et les `:id` de `ID_EDIT_FORM_ROUTES`), `read`
   * pour tout le reste. Les actions à l'intérieur des écrans restent en plus
   * gardées par les méthodes can*.
   * Sinon : fallback legacy `data.roles`. `super_admin` toujours autorisé.
   *
   * @param employeeRole rôle lu par le guard (lecture fraîche d'employees/) —
   *                     pas currentAbility, pour rester fidèle au flux guard.
   */
  routeAllowed(
    routePath: string,
    routeRoles: string[],
    employeeRole: string,
    explicitRbac?: { module: RbacModule; subModule: string } | null
  ): boolean {
    if (employeeRole === 'super_admin') return true;

    const mapping = explicitRbac ?? resolveRouteRbacMapping(routePath);
    if (
      this.rbacEnabled &&
      mapping &&
      mapping.module !== 'admin' &&
      this.rbacService.isModuleCovered(mapping.module)
    ) {
      if (employeeRole === 'admin') return true;
      // #935 — le verbe suit l'écran (convention du 04/08) : `create` pour un
      // formulaire de création, `update` pour une édition, `read` sinon. Avant,
      // 47 écrans d'écriture étaient décidés sur le droit de lecture.
      return this.rbacService.can(mapping.module, mapping.subModule, requiredRouteOperation(routePath));
    }

    return routeRoles.includes(employeeRole);
  }

  /**
   * #813 — attente bornée avant une décision de guard : config (flag) chargée,
   * et si le flag est actif, permissions matrice résolues. Le TOUT est borné
   * à 5 s (y compris un appConfig.init() qui pendrait) : ne bloque jamais la
   * navigation — à l'expiration, coveredModules est vide et routeAllowed
   * retombe sur le legacy (pas de lockout).
   */
  async whenReady(): Promise<void> {
    const load = (async () => {
      await this.appConfig.init();
      if (!this.appConfig.rbacEnabled) return;
      await firstValueFrom(this.rbacService.permissions$.pipe(
        filter(p => p !== null),
        take(1),
      ));
    })().catch(() => undefined); // erreur ⇒ décision legacy
    await Promise.race([
      load,
      new Promise<void>(resolve => setTimeout(resolve, 5000)),
    ]);
  }

  /**
   * Check if user can access a specific sidebar route (sub-module level).
   * Used to show/hide individual nav items within a section.
   */
  canAccessRoute(route: string): boolean {
    if (this.rbacEnabled) {
      if (this.isAdmin()) return true;
      const mapping = ROUTE_TO_RBAC[route];
      if (!mapping) return true; // unmapped routes are visible
      // Module non couvert par la matrice ⇒ legacy (toutes les routes du module visibles)
      if (!this.rbacService.isModuleCovered(mapping.module)) return true;
      return this.rbacService.can(mapping.module, mapping.subModule, 'read');
    }
    // Legacy: if you can see the module, you can see the routes
    return true;
  }
}
